import {
  AREA_TYPE_LABELS,
  COMPANY_LABELS,
  RHYTHM_STATUS_LABELS,
  daysUntil,
  formatDate,
  pluralize,
  type CoverageDto,
  type CoverageAreaDto,
  type RhythmStatus,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, CircleDashed, CircleAlert } from 'lucide-react';
import { useFilter } from '../../app/FilterProvider.js';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { api } from '../../lib/api.js';
import { useAreaColor } from '../../lib/area-color.js';
import { cn } from '../../lib/cn.js';

/**
 * The plan's pulse per product area (07 — 2.2.1): how long since something
 * went out, how long until the next, measured against the area's own beat.
 * This is the surface that answers «are we forgetting a product?» — the
 * timeline answers «what is happening with each item».
 */
export function RhythmView() {
  const { company, areaId } = useFilter();
  const areaColor = useAreaColor();

  const { data, isLoading } = useQuery({
    queryKey: ['coverage'],
    queryFn: () => api.get<CoverageDto>('/content/coverage'),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) return <SkeletonPanel lines={8} />;

  const visible = data.areas.filter(
    (area) =>
      (!company || area.company === company || area.company === 'BEGGE') &&
      (!areaId || area.areaId === areaId),
  );

  if (visible.length === 0) {
    return (
      <GlassPanel>
        <EmptyState title="Ingen områder å måle rytmen på med dette filteret." />
      </GlassPanel>
    );
  }

  // The whole point of the surface: what is about to be forgotten sits on top —
  // quiet areas first, then slipping, longest silence first within each.
  const ORDER: Record<RhythmStatus, number> = { QUIET: 0, SLIPPING: 1, ON_TRACK: 2 };
  const rows = [...visible].sort(
    (a, b) => ORDER[a.rhythm] - ORDER[b.rhythm] || daysSince(b) - daysSince(a),
  );

  const quiet = rows.filter((r) => r.rhythm === 'QUIET').length;
  const slipping = rows.filter((r) => r.rhythm === 'SLIPPING').length;

  return (
    <div className="space-y-3" data-testid="rhythm">
      <p className="text-ink-soft text-sm">
        Målt mot {pluralize(data.weeks, 'uke', 'uker')} fram:{' '}
        {quiet > 0 && (
          <span className="text-danger">{pluralize(quiet, 'område', 'områder')} stille</span>
        )}
        {quiet > 0 && slipping > 0 && ', '}
        {slipping > 0 && <span className="text-warning">{slipping} på etterskudd</span>}
        {quiet === 0 && slipping === 0 && 'alle områder er i rytme.'}
        {(quiet > 0 || slipping > 0) && '.'}
      </p>

      <GlassPanel padded={false}>
        <ul className="divide-hairline divide-y">
          {rows.map((area) => (
            <RhythmRow key={area.areaId} area={area} color={areaColor(area.color)} />
          ))}
        </ul>
      </GlassPanel>

      <p className="text-ink-soft/70 text-[11px] leading-relaxed">
        Rytmen settes per område i Admin → Områder. Områder uten egen rytme måles mot planleggerens
        felles standard — merket «standard» her.
      </p>
    </div>
  );
}

const TONES: Record<RhythmStatus, { className: string; Icon: typeof CircleCheck }> = {
  ON_TRACK: { className: 'bg-success/12 text-success border-success/25', Icon: CircleCheck },
  SLIPPING: { className: 'bg-warning/12 text-warning border-warning/25', Icon: CircleDashed },
  QUIET: { className: 'bg-danger/12 text-danger border-danger/25', Icon: CircleAlert },
};

function daysSince(area: CoverageAreaDto): number {
  return area.lastPublishedAt ? -daysUntil(area.lastPublishedAt) : Number.MAX_SAFE_INTEGER;
}

function RhythmRow({ area, color }: { area: CoverageAreaDto; color: string }) {
  const tone = TONES[area.rhythm];
  const since = area.lastPublishedAt ? -daysUntil(area.lastPublishedAt) : null;
  const until = area.nextPlannedAt ? daysUntil(area.nextPlannedAt) : null;

  // How far through the beat the area is — full bar means the beat is broken.
  const progress = Math.min(1, (since ?? area.cadenceDays) / area.cadenceDays);

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <span
          aria-hidden
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        <div className="min-w-0">
          <p className="text-ink truncate text-sm">{area.name}</p>
          <p className="text-ink-soft truncate text-[11px]">
            {AREA_TYPE_LABELS[area.type]} · {COMPANY_LABELS[area.company]} · mål: hver{' '}
            {area.cadenceDays}. dag{!area.hasOwnCadence && ' (standard)'}
          </p>
        </div>
      </div>

      <div className="flex flex-1 items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex justify-between gap-2 text-[11px]">
            <span className="text-ink-soft truncate">
              {since === null
                ? 'Aldri publisert'
                : since === 0
                  ? 'Publisert i dag'
                  : `Sist for ${pluralize(since, 'dag', 'dager')} siden`}
            </span>
            <span className={cn('truncate', until === null ? 'text-danger' : 'text-ink-soft')}>
              {until === null
                ? 'Ingenting planlagt'
                : until === 0
                  ? 'Neste i dag'
                  : `Neste ${formatDate(area.nextPlannedAt!)}`}
            </span>
          </div>
          <div
            className="bg-raised/80 mt-1 h-1.5 overflow-hidden rounded-full"
            role="img"
            aria-label={`${Math.round(progress * 100)} % av rytmen brukt`}
          >
            <div
              className={cn(
                'h-full rounded-full transition-all',
                area.rhythm === 'ON_TRACK'
                  ? 'bg-success'
                  : area.rhythm === 'SLIPPING'
                    ? 'bg-warning'
                    : 'bg-danger',
              )}
              style={{ width: `${Math.max(4, progress * 100)}%` }}
            />
          </div>
        </div>

        <span className="num-tabular text-ink-soft w-14 shrink-0 text-right text-[11px]">
          {area.planned}/{area.target} <span className="block">planlagt</span>
        </span>

        <span
          className={cn(
            'inline-flex w-32 shrink-0 items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
            tone.className,
          )}
        >
          <tone.Icon size={13} strokeWidth={2.2} aria-hidden />
          {RHYTHM_STATUS_LABELS[area.rhythm]}
        </span>
      </div>
    </li>
  );
}
